import {
  getMeristemContext as requestMeristemContext,
  Context,
} from "@noom/meristem-context";

import { CityResponse } from "maxmind";

import { captureException } from "@utils/sentry";

export interface MeristemContext {
  context: Context;
  sha: string;
  meristemState: MeristemState;
  experimentsCookie: ExperimentDefinition[];
  error?: string;
}

export interface MeristemState {
  id: number;
  owner: string;
  repo?: string;
  baseline: string;
  description: string;
  experiments: MeristemExperiment[];
}

export interface MeristemExperiment {
  name: string;
  targeting: Targeting[];
  variations: Variation[];
  isGreedyTargeting?: boolean;

  // Not found upstream but referenced in our code
  funnelId?: string;
  startEvents: {
    name: string;
    filters: Targeting[];
  }[];
}
export interface Targeting {
  key: string;
  operator: string;
  value: string | string[];
}
interface Variation {
  name: string;
  repo?: string;
  sha: string;
  weight: number;
}

export interface ExperimentDefinition {
  // This name is mapped to experiment.id from meristem state.
  experimentName: string;
  variationName: string;
  sha: string;
  shaOverride?: string;
  repo?: string;
}

const defaultMeristemContext: Context = {
  device_brand: "Apple",
  device_family: "Mac",
  device_is_mobile: false,
  device_os: "Mac OS X",
  city_name: "New York",
  continent_code: "NA",
  country_code: "US",
  is_eu_citizen: false,
  postal_code: "10001",
  subdivision: "NY",
  is_forced_country_code: false,
  language_code: "en",
  route_id: "_direct",
  pathname: "",
  route: null,
};

// This is a mocked response from the MaxMind database (CityResponse)
const defaultMaxmindDBResponse: CityResponse = {
  country: {
    geoname_id: 1234,
    iso_code: "US",
    names: {
      en: "United State",
    },
  },
  continent: {
    geoname_id: 1234,
    code: "NA",
    names: {
      en: "North America",
    },
  },
  city: {
    geoname_id: 1234,
    names: {
      en: "Queens",
    },
  },
  postal: {
    code: "11375",
  },
  subdivisions: [
    {
      geoname_id: 5128638,
      iso_code: "NY",
      names: {
        en: "New York",
      },
    },
  ],
};
const globalMeristemContext: MeristemContext = (window as any).meristemContext;

/**
 * Returns the meristemContext passed by buyflow-worker, or it fallbacks to default for development environment
 */
export function getMeristemContext() {
  return globalMeristemContext?.context || getMeristemDefaultValue();
}

export function getExperimentState() {
  return globalMeristemContext?.experimentsCookie || [];
}

export function getMeristemState() {
  return globalMeristemContext?.meristemState;
}

/**
 * On staging/production this method will never be called since window.meristemContext will be used instead
 */
function getMeristemDefaultValue(): Context {
  if (process.env.NODE_ENV === "development") {
    const userAgent = window?.navigator?.userAgent;
    const url = new URL(window.location.href);
    const response = requestMeristemContext(
      url,
      userAgent,
      defaultMaxmindDBResponse,
      ""
    );

    if ("redirectUrl" in response) {
      return defaultMeristemContext;
    }

    return { ...defaultMeristemContext, ...response };
  }

  if (process.env.NODE_ENV === "production") {
    const sentryErrorName = "MeristemContextDefaulted-UnavailableOnWindow";

    // We want to trigger sentry error if the meristem context defaults for the production environment
    captureException(new Error(sentryErrorName), sentryErrorName);
  }
  return defaultMeristemContext;
}
